import { getApiBaseUrl } from "../api/baseUrl"
import { throwIfRateLimited } from "../api/rateLimit"
import type {
  BulkCardSyncResponse,
  BulkDeckSyncResponse,
  BulkHeroSyncResponse,
  CardLibraryListResponse,
  CardLibraryStore,
  DeckLibraryListResponse,
  DeckLibraryStore,
  HeroLibraryListResponse,
  HeroLibraryStore,
  ManagedCardRecord,
  ManagedCardSyncResponse,
  ManagedDeckRecord,
  ManagedDeckSyncResponse,
  ManagedHeroRecord,
  ManagedHeroSyncResponse,
} from "../schemas"
import { LibraryError } from "./errors"

async function postJson<TResponse>(
  path: string,
  sessionToken: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<TResponse> {
  const baseUrl = getApiBaseUrl()
  if (!baseUrl) {
    throw new LibraryError("LIBRARY_SYNC_NO_WORKER")
  }

  const response = await fetch(new URL(path, baseUrl), {
    method: "POST",
    headers: {
      Authorization: `Bearer ${sessionToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
  })

  throwIfRateLimited(response)

  if (!response.ok) {
    const message = await response.text()
    throw new LibraryError("LIBRARY_SYNC_FAILED", message || undefined)
  }

  return (await response.json()) as TResponse
}

async function getJson<TResponse>(
  path: string,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<TResponse> {
  const baseUrl = getApiBaseUrl()
  if (!baseUrl) {
    throw new LibraryError("LIBRARY_SYNC_NO_WORKER")
  }

  const response = await fetch(new URL(path, baseUrl), {
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    },
    signal,
  })

  throwIfRateLimited(response)

  if (!response.ok) {
    const message = await response.text()
    throw new LibraryError("LIBRARY_FETCH_FAILED", message || undefined)
  }

  return (await response.json()) as TResponse
}

async function sendWithoutBody(
  path: string,
  sessionToken: string,
  method: "DELETE",
  signal?: AbortSignal,
): Promise<void> {
  const baseUrl = getApiBaseUrl()
  if (!baseUrl) {
    throw new LibraryError("LIBRARY_SYNC_NO_WORKER")
  }

  const response = await fetch(new URL(path, baseUrl), {
    method,
    headers: {
      Authorization: `Bearer ${sessionToken}`,
    },
    signal,
  })

  throwIfRateLimited(response)

  if (!response.ok) {
    const message = await response.text()
    throw new LibraryError("LIBRARY_DELETE_FAILED", message || undefined)
  }
}

async function deleteJson(
  path: string,
  sessionToken: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<void> {
  const baseUrl = getApiBaseUrl()
  if (!baseUrl) {
    throw new LibraryError("LIBRARY_SYNC_NO_WORKER")
  }

  const response = await fetch(new URL(path, baseUrl), {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${sessionToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
    signal,
  })

  throwIfRateLimited(response)

  if (!response.ok) {
    const message = await response.text()
    throw new LibraryError("LIBRARY_DELETE_FAILED", message || undefined)
  }
}

export async function syncManagedCardRecordToServer(
  record: ManagedCardRecord,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<ManagedCardRecord> {
  const response = await postJson<ManagedCardSyncResponse>(
    "/api/library/cards",
    sessionToken,
    {
      record,
    },
    signal,
  )
  return response.record
}

export async function syncManagedHeroRecordToServer(
  record: ManagedHeroRecord,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<ManagedHeroRecord> {
  const response = await postJson<ManagedHeroSyncResponse>(
    "/api/library/heroes",
    sessionToken,
    {
      record,
    },
    signal,
  )
  return response.record
}

export async function syncManagedDeckRecordToServer(
  record: ManagedDeckRecord,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<ManagedDeckRecord> {
  const response = await postJson<ManagedDeckSyncResponse>(
    "/api/library/decks",
    sessionToken,
    {
      record,
    },
    signal,
  )
  return response.record
}

export async function fetchManagedCardRecordsFromServer(
  sessionToken: string,
  signal?: AbortSignal,
): Promise<CardLibraryStore> {
  const response = await getJson<CardLibraryListResponse>(
    "/api/library/cards",
    sessionToken,
    signal,
  )
  return response.records
}

export async function fetchManagedCardRecordFromServer(
  cardId: string,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<ManagedCardRecord> {
  return getJson<ManagedCardRecord>(
    `/api/library/cards/${cardId}.json`,
    sessionToken,
    signal,
  )
}

export async function fetchManagedHeroRecordsFromServer(
  sessionToken: string,
  signal?: AbortSignal,
): Promise<HeroLibraryStore> {
  const response = await getJson<HeroLibraryListResponse>(
    "/api/library/heroes",
    sessionToken,
    signal,
  )
  return response.records
}

export async function fetchManagedHeroRecordFromServer(
  heroId: string,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<ManagedHeroRecord> {
  return getJson<ManagedHeroRecord>(
    `/api/library/heroes/${heroId}.json`,
    sessionToken,
    signal,
  )
}

export async function fetchManagedDeckRecordsFromServer(
  sessionToken: string,
  signal?: AbortSignal,
): Promise<DeckLibraryStore> {
  const response = await getJson<DeckLibraryListResponse>(
    "/api/library/decks",
    sessionToken,
    signal,
  )
  return response.records
}

export async function fetchManagedDeckRecordFromServer(
  deckId: string,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<ManagedDeckRecord> {
  return getJson<ManagedDeckRecord>(
    `/api/library/decks/${deckId}.json`,
    sessionToken,
    signal,
  )
}

export function deleteManagedCardRecordFromServer(
  cardId: string,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<void> {
  return sendWithoutBody(
    `/api/library/cards/${cardId}.json`,
    sessionToken,
    "DELETE",
    signal,
  )
}

export function deleteManagedHeroRecordFromServer(
  heroId: string,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<void> {
  return sendWithoutBody(
    `/api/library/heroes/${heroId}.json`,
    sessionToken,
    "DELETE",
    signal,
  )
}

export function deleteManagedDeckRecordFromServer(
  deckId: string,
  sessionToken: string,
): Promise<void> {
  return sendWithoutBody(
    `/api/library/decks/${deckId}.json`,
    sessionToken,
    "DELETE",
  )
}

export async function bulkSyncManagedCardRecordsToServer(
  records: ManagedCardRecord[],
  sessionToken: string,
  signal?: AbortSignal,
): Promise<BulkCardSyncResponse> {
  return postJson<BulkCardSyncResponse>(
    "/api/library/cards/bulk",
    sessionToken,
    { records },
    signal,
  )
}

export function bulkDeleteManagedCardRecordsFromServer(
  ids: string[],
  sessionToken: string,
  signal?: AbortSignal,
): Promise<void> {
  return deleteJson("/api/library/cards/bulk", sessionToken, { ids }, signal)
}

export async function bulkSyncManagedHeroRecordsToServer(
  records: ManagedHeroRecord[],
  sessionToken: string,
  signal?: AbortSignal,
): Promise<BulkHeroSyncResponse> {
  return postJson<BulkHeroSyncResponse>(
    "/api/library/heroes/bulk",
    sessionToken,
    { records },
    signal,
  )
}

export function bulkDeleteManagedHeroRecordsFromServer(
  ids: string[],
  sessionToken: string,
  signal?: AbortSignal,
): Promise<void> {
  return deleteJson("/api/library/heroes/bulk", sessionToken, { ids }, signal)
}

export async function bulkSyncManagedDeckRecordsToServer(
  records: ManagedDeckRecord[],
  sessionToken: string,
  signal?: AbortSignal,
): Promise<BulkDeckSyncResponse> {
  return postJson<BulkDeckSyncResponse>(
    "/api/library/decks/bulk",
    sessionToken,
    { records },
    signal,
  )
}

export function bulkDeleteManagedDeckRecordsFromServer(
  ids: string[],
  sessionToken: string,
  signal?: AbortSignal,
): Promise<void> {
  return deleteJson("/api/library/decks/bulk", sessionToken, { ids }, signal)
}
